// react
import { Suspense, useEffect, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui icons
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  darken,
  Fade,
  Grid,
  lighten,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import PowerLoadProfilesAnalysisChart from '@/features/energyxplorer/components/analysis/PowerLoadProfilesAnalysisChart';
import EnergyTreeNodeTraversal from '@/features/energyxplorer/components/nodes/EnergyTreeNodeTraversal';

// feature-hooks
import useEnergyGroupUnit from '@/features/energyxplorer/hooks/useEnergyGroupType';
import { useEnergyTreeNode } from '@/features/energyxplorer/hooks/useEnergyTreeNode';
import useUrlAnalysisScope from '@/features/energyxplorer/hooks/useUrlAnalysisScope';
import useUrlSeu from '@/features/energyxplorer/hooks/useUrlSeu';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import {
  useGetPowerLoadProfileAnalysisQuery,
  useGetPowerLoadProfileAnalysisSeuQuery,
} from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';
import EnergyTreeNodeAnalysisToolbar from './EnergyTreeNodeAnalysisToolbar';

import { DateTime } from 'luxon';

const HeaderDisplay = ({ label, description }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        mb: 2,
      }}
    >
      <Typography variant="body1" color="textSecondary" fontWeight="bold">
        {label}
      </Typography>
      <Tooltip title={description} placement="right">
        <InfoRoundedIcon
          fontSize="small"
          sx={{
            color: (theme) =>
              theme.palette.mode === 'light' ? 'lightgray' : 'gray',
            ml: 1,
          }}
        />
      </Tooltip>
    </Box>
  );
};

const ValueDisplay = ({
  label = 'label',
  value = 0,
  unit = 'kWh',
  trend = 0,
}) => {
  const trendLabel = `${trend > 0 ? '+' : ''}${trend.toFixed(0)}%`;
  const trendColor = (() => {
    if (trend >= 10) return 'error';
    if (trend >= 2) return 'warning';
    if (trend >= -2) return 'primary';
    return 'success';
  })();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
        <Box
          sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
        >
          <Typography variant="h4" color="textPrimary" fontWeight={'bold'}>
            {value} {unit}
          </Typography>
          <Chip
            sx={{
              marginLeft: 1,
              fontWeight: 'bold',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? lighten(theme.palette[trendColor].main, 0.9)
                  : darken(theme.palette[trendColor].main, 0.8),
              color: (theme) => theme.palette[trendColor].dark,
              border: (theme) => `1px solid ${theme.palette[trendColor].light}`,
            }}
            label={trendLabel}
            size={'small'}
          />
        </Box>
      </Box>
    </Box>
  );
};

// MAKE CHIP CARD AND MAKE IT LIKE INITIATIVES TAB

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

const EnergyTreeNodePowerLoadProfilesAnalysisPage = () => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  const { timeRange } = useUrlDateRange();
  const { accountId, energyGroupId } = useParams({ strict: false });
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );
  const { nodeId, energyTreeNode, setNodeId } = useEnergyTreeNode({
    extraSkip: !selectedEnergyGroup, // don’t fetch if group not loaded
  });

  const [analysisScope] = useUrlAnalysisScope({
    allowed: ['node', 'seu'],
    fallback: 'node',
  });

  const selectedRange = [
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ];

  // ==== SO FAR SO GOOD ======== //

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach((node) => {
      const type = node.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const validSeuOptions = useMemo(() => {
    return (
      Array.from(nodeGroupsByType.entries())
        // eslint-disable-next-line no-unused-vars
        .filter(([type, nodes]) =>
          nodes.some((node) => node.dataStreams?.length > 0),
        )
        .map(([type]) => type)
        .filter(
          (type) =>
            type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle',
        )
    );
  }, [nodeGroupsByType]);

  const [seuToAnalyse] = useUrlSeu({
    key: 'type',
    allowed: validSeuOptions,
    fallback: () => validSeuOptions[0] ?? null,
  });
  const [filtersOpen, setFiltersOpen] = useState(true);

  const {
    data: nodeAnalysisData,
    isFetching: nodeAnalysisDataFetching,
    isError: nodeAnalysisError,
  } = useGetPowerLoadProfileAnalysisQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      nodeId: nodeId,
      startTimestamp: timeRange.start,
      endTimestamp: timeRange.end,
    },
    { skip: !nodeId || !selectedEnergyGroup || !accountId },
  );

  const {
    data: seuAnalysisData,
    isFetching: seuAnalysisDataFetching,
    isError: seuAnalysisError,
  } = useGetPowerLoadProfileAnalysisSeuQuery(
    {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      seuType: seuToAnalyse,
      startTimestamp: timeRange.start,
      endTimestamp: timeRange.end,
    },
    { skip: !seuToAnalyse || !selectedEnergyGroup || !accountId },
  );

  useEffect(() => {
    if (!energyTreeNode && selectedEnergyGroup?.energyTreeNodes?.length > 0) {
      setNodeId(selectedEnergyGroup?.energyTreeNodes?.[0]?.energyTreeNodeId);
    }
  }, [energyTreeNode, selectedEnergyGroup, setNodeId]);

  // Errors from power load profile analysis queries are handled by apiErrorMiddleware.

  const nodeData = nodeAnalysisData?.PowerLoadProfileData;
  const seuData = seuAnalysisData?.PowerLoadProfileData;

  const nodePowerData = nodeAnalysisData?.PowerData;
  const seuPowerData = seuAnalysisData?.PowerData;

  const loadData = analysisScope === 'node' ? nodeData : seuData;
  const powerData = analysisScope === 'node' ? nodePowerData : seuPowerData;

  const nodeSummary = nodeAnalysisData?.PowerLoadProfileSummary;
  const seuSummary = seuAnalysisData?.PowerLoadProfileSummary;

  const summaryData = analysisScope === 'node' ? nodeSummary : seuSummary;

  const analysisError =
    analysisScope === 'node' ? nodeAnalysisError : seuAnalysisError;

  const readyToRender = energyTreeNode && selectedEnergyGroup;
  return (
    <PageContainer sx={{ pt: 0 }}>
      {readyToRender && (
        <Fade in={readyToRender} timeout={500}>
          <Grid container spacing={2}>
            <Cards data={summaryData} />
            <Grid
              sx={{ transition: 'flex-basis 0.3s ease' }}
              size={{
                md: 12,
                lg: filtersOpen ? 9 : 12
              }}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      {analysisScope === 'node'
                        ? `${energyTreeNode?.name} (Node)`
                        : `${seuToAnalyse} (SEU)`}
                      <InfoTooltipIcon
                        title={t(
                          'power_load_profiles.tooltips.power_load_profiles_description',
                        )}
                      />
                    </Box>
                  }
                  subheader={
                    selectedRange[0] && selectedRange[1]
                      ? `${t('power_load_profiles.in_period')} ${formatDate(
                          selectedRange[0],
                        )} - ${formatDate(selectedRange[1])}`
                      : t('power_load_profiles.select_period')
                  }
                  action={
                    <EnergyTreeNodeAnalysisToolbar
                      filtersOpen={filtersOpen}
                      onToggleFilters={() => setFiltersOpen(!filtersOpen)}
                      showFilterToggle
                      filterToggleTooltip={{
                        open: t('hour_analysis.tooltips.hide'),
                        closed: t('hour_analysis.tooltips.show'),
                      }}
                    />
                  }
                />
                <CardContent sx={{ minHeight: 400 }}>
                  {!analysisError && (
                    <Suspense
                      fallback={
                        <div>
                          {t('analysis.loading', { ns: 'energyxplorer' })}
                        </div>
                      }
                    >
                      <PowerLoadProfilesAnalysisChart
                        loadData={loadData}
                        powerData={powerData}
                        isLoading={
                          nodeAnalysisDataFetching || seuAnalysisDataFetching
                        }
                      />
                    </Suspense>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {filtersOpen && (
              <Grid
                className="non-printable"
                size={{
                  md: 12,
                  lg: 3
                }}>
                <Grid size="grow">
                  <EnergyTreeNodeTraversal showModeToggle={true} />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Fade>
      )}
    </PageContainer>
  );
};

export default EnergyTreeNodePowerLoadProfilesAnalysisPage;

const Cards = ({ data }) => {
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const { unit } = useEnergyGroupUnit();

  return (
    <>
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Card variant="outlined">
          <CardContent>
            <HeaderDisplay
              label={t('power_load_profiles.base')}
              description={t('power_load_profiles.tooltips.base_description')}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.BaseLoadSummary?.Average.toFixed(0)}
              unit={unit}
              trend={data?.BaseLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Card variant="outlined">
          <CardContent>
            <HeaderDisplay
              label={t('power_load_profiles.intermediate')}
              description={t(
                'power_load_profiles.tooltips.intermediate_description',
              )}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.IntermediateLoadSummary?.Average.toFixed(0)}
              unit={unit}
              trend={data?.IntermediateLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 4
        }}>
        <Card variant="outlined">
          <CardContent>
            <HeaderDisplay
              label={t('power_load_profiles.peak')}
              description={t('power_load_profiles.tooltips.peak_description')}
            />
            <ValueDisplay
              label={t('common.mean')}
              value={data?.PeakLoadSummary?.Average.toFixed(0)}
              unit={unit}
              trend={data?.PeakLoadSummary?.TrendPercentage}
            />
          </CardContent>
        </Card>
      </Grid>
    </>
  );
};
